import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import {
    REQUEST_CLIMBS,
    REQUEST_CLIMBS_ON_ROUTE,
    REQUEST_CLIMBS_ON_COURSE,
    REQUEST_SECONDARY_CLIMBS
} from '../actions/ActionTypes';
import { gcApi } from '../config/axios';
import {
    receiveClimbs,
    receiveClimbsOnRoute,
    receiveClimbsOnCourse,
    receiveSecondaryClimbs,
    receiveClimbsOnCourseFailed
} from '../actions';
import { isChinaUser } from '../utils/ChinaUtil';
import BaiduConvertService from '../Map/utils/BadiuConvertService';
import MapUtil from 'backbone/views/utils/LeafletMap/utils/Util';
import { getCourseDetail } from 'reducers/course/courses';

// import DTO typedefs
import 'utils/climbs/climbsTypeDefs';

export function* fetchClimbs(action) {
    // connectapitest-swagger.garmin.com/?urls.primaryName=course-service#/Courses/getClimbsInBoundingBox
    // climbTypePreference
    // Available values : PAVED, MIXED_FAVOR_UNPAVED, UNPAVED

    // minDifficulty
    // Available values : NONE, HORS_CATEGORY, FIRST_CATEGORY, SECOND_CATEGORY, THIRD_CATEGORY, FOURTH_CATEGORY, FIFTH_CATEGORY, INVALID
    // maxDifficulty
    // Available values : NONE, HORS_CATEGORY, FIRST_CATEGORY, SECOND_CATEGORY, THIRD_CATEGORY, FOURTH_CATEGORY, FIFTH_CATEGORY, INVALID

    try {
        const {
            north,
            south,
            east,
            west,
            surface = 'MIXED_FAVOR_UNPAVED',
            minDifficulty,
            maxDifficulty
        } = action.boundingBox;
        const url = `/course-service/course/climbs/primary?north=${north}&south=${south}&east=${east}&west=${west}&climbTypePreference=${surface}&minDifficulty=${minDifficulty}&maxDifficulty=${maxDifficulty}`;
        const response = yield gcApi.get(url);

        if (isChinaUser() && response && response.data) {
            const convertService = new BaiduConvertService();
            let climbDataIndex = 0;
            for (let climbData of response.data) {
                if (climbData && climbData.primaryClimbs) {
                    let primaryClimbs = climbData.primaryClimbs;
                    for (let primaryClimb of primaryClimbs) {
                        if (primaryClimb && primaryClimb.polylineDTO) {
                            let polylineDTO = primaryClimb.polylineDTO;
                            let convertResponse = yield convertService.fetchGenerator(polylineDTO, true);
                            if (convertResponse) {
                                convertResponse.forEach((convertedPoint, index) => {
                                    polylineDTO[index].latitude = convertedPoint.lat;
                                    polylineDTO[index].longitude = convertedPoint.lon;
                                });
                            }
                        }
                    }
                }
                // return converted data for every 30 ployline to avoid too much loading time
                if (climbDataIndex > 0 && climbDataIndex % 30 === 0) {
                    yield put(receiveClimbs(response.data));
                }
                climbDataIndex++;
            }
        }

        yield put(receiveClimbs(response.data));
    } catch (e) {
        yield put(receiveClimbsOnCourseFailed());
    }
}

export function* fetchClimbsOnRoute(action) {
    // https://connectapi.garmin.com/course-service/swagger/index.html#/Courses/getClimbsOnRoute
    // body: List<GeoPointDTO>

    try {
        if (action.coursePoints.length) {
            const response = yield gcApi.post(`/course-service/course/climbs/onroute`, action.coursePoints);
            yield put(receiveClimbsOnRoute(response.data, action.historyIndex));
        } else {
            yield put(receiveClimbsOnRoute(false));
        }
    } catch (e) {}
}

/**
 * convert `fetchClimbsOnCourse` response data for baidu map
 * @param {ClimbDTO[]} data
 * @param {number} courseId
 */
export function* processBaiduClimbsOnCourse(data, courseId) {
    if (!data || !data.length) {
        yield put(receiveClimbsOnCourse(data));
        return;
    }

    /**
     * @type {{latitude: number; longitude: number}[]} points to convert
     */
    const points = [];

    for (let index = 0; index < data.length; index += 1) {
        const climbDto = data[index];
        const geoPointDtos = climbDto.polylineDTO;
        if (!geoPointDtos || !geoPointDtos.length) {
            continue;
        }
        geoPointDtos.forEach((item) => {
            points.push({ latitude: item.latitude, longitude: item.longitude });
        });
    }

    if (!points.length) {
        /** no data to convert. exit */
        yield put(receiveClimbsOnCourse(data));
        return;
    }

    let originalCoordinateSystem = null;

    /**
     * @type {CourseDTO}
     */
    let courseDto = yield select(getCourseDetail, courseId);

    if (!courseDto) {
        // we usually fetch climbs on course together with course, so we wait 500 ms for courseDto
        yield delay(500);
        courseDto = yield select(getCourseDetail, courseId);
        if (!courseDto) {
            // if still not fetched, we call summuried api instead

            // swagger: https://connectapitest-swagger.garmin.com/?urls.primaryName=course-service#/Courses/getSummarizedCourse
            const response = yield call(gcApi.get, `/course-service/course/summary/${courseId}`);
            originalCoordinateSystem = response.data.originalCoordinateSystem;
        }
    } else {
        originalCoordinateSystem = courseDto.originalCoordinateSystem;
    }

    const fromGcj02 = originalCoordinateSystem === MapUtil.CoordinateSystem.GCJ02;

    const convertService = new BaiduConvertService();

    /**
     * @type {{ lat: number; lon: number }[]} converted response
     */
    const convertedResponse = yield convertService.fetchGenerator(points, fromGcj02);

    let cursor = 0;

    for (let index = 0; index < data.length; index += 1) {
        const climbDto = data[index];
        const geoPointDtos = climbDto.polylineDTO;
        if (!geoPointDtos || !geoPointDtos.length) {
            continue;
        }
        const sliced = convertedResponse.slice(cursor, cursor + geoPointDtos.length);
        cursor += geoPointDtos.length;

        /** override response */
        geoPointDtos.forEach((item, index) => {
            item.latitude = sliced[index].lat;
            item.longitude = sliced[index].lon;
        });
    }

    yield put(receiveClimbsOnCourse(data));
}

export function* fetchClimbsOnCourse(action) {
    // https://connectapi.garmin.com/course-service/swagger/index.html#/Courses/getClimbsOnRoute_1
    // courseId

    try {
        const response = yield gcApi.get(`/course-service/course/climbs/onroute/${action.courseId}`);

        if (isChinaUser()) {
            yield fork(processBaiduClimbsOnCourse, response.data, action.courseId);
            return;
        }
        yield put(receiveClimbsOnCourse(response.data));
    } catch (e) {}
}

export function* fetchSecondaryClimbs(action) {
    // https://connectapitest-swagger.garmin.com/?urls.primaryName=course-service#/Courses/getSecondaryClimbs
    // path: courseId
    // query params: climbFileId, targetCoordinateSystem, primaryClimbStartLat, primaryClimbStartLon

    try {
        let climbQuery;

        if (action.climb.length > 1) {
            climbQuery = action.climb
                .map((climb, index) => {
                    if (index === 0) {
                        return `?climbId=${climb.gmapClimbId}`;
                    } else {
                        return `&climbId=${climb.gmapClimbId}`;
                    }
                })
                .join('');
        } else {
            climbQuery = `?climbId=${action.climb[0].gmapClimbId}`;
        }

        const climbData = action.climb[0]; // it's the same data even if multiple climb Ids are queried
        const minDifficulty =
            action.minDifficulty === 'FIFTH_CATEGORY' || !action.minDifficulty ? 'NONE' : action.minDifficulty;
        const maxDifficulty = action.maxDifficulty ? action.maxDifficulty : 'HORS_CATEGORY';

        const response = yield gcApi.get(
            `/course-service/course/climbs/secondary/${climbQuery}&climbFileId=${climbData.gmapClimbFileId}&primaryClimbStartLat=${climbData.polylineDTO[0].latitude}&primaryClimbStartLon=${climbData.polylineDTO[0].longitude}&minDifficulty=${minDifficulty}&maxDifficulty=${maxDifficulty}`
        );

        if (isChinaUser() && response && response.data) {
            const convertService = new BaiduConvertService();
            for (let climbData of response.data) {
                if (climbData && climbData.polylineDTO) {
                    let polylineDTO = climbData.polylineDTO;
                    let convertResponse = yield convertService.fetchGenerator(polylineDTO, true);
                    if (convertResponse) {
                        convertResponse.forEach((convertedPoint, index) => {
                            polylineDTO[index].latitude = convertedPoint.lat;
                            polylineDTO[index].longitude = convertedPoint.lon;
                        });
                    }
                }
            }
        }

        yield put(receiveSecondaryClimbs(response.data));
    } catch (e) {}
}

export const climbSagas = [
    takeEvery(REQUEST_CLIMBS, fetchClimbs),
    takeEvery(REQUEST_CLIMBS_ON_ROUTE, fetchClimbsOnRoute),
    takeEvery(REQUEST_CLIMBS_ON_COURSE, fetchClimbsOnCourse),
    takeEvery(REQUEST_SECONDARY_CLIMBS, fetchSecondaryClimbs)
];
